import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Shield, ArrowLeft, Users, Plus, Trash2, ChevronLeft, ChevronRight, Mail, HeartHandshake, Sparkles, Printer, Download, ScrollText, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import AppFooter from '../components/AppFooter';
import BudgetPrayerModal from '../components/BudgetPrayerModal';
import ContentmentTags from '../components/ContentmentTags';
import HouseholdCouncilCard from '../components/HouseholdCouncilCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VERSES = [
  { t: '"The plans of the diligent lead to profit as surely as haste leads to poverty."', r: 'Proverbs 21:5' },
  { t: '"Suppose one of you wants to build a tower. Won\'t you first sit down and estimate the cost?"', r: 'Luke 14:28' },
  { t: '"Honor the Lord with your wealth, with the firstfruits of all your crops."', r: 'Proverbs 3:9' },
  { t: '"Each of you should give what you have decided in your heart to give, not reluctantly."', r: '2 Corinthians 9:7' },
];

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const monthLabel = (m) =>
  new Date(`${m}-15T12:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const shiftMonth = (m, delta) => {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo - 1 + delta, 15);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

function SetupView({ onReady, prefillCode }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState(prefillCode || '');
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/household`, { name: name.trim() || 'Our Family' });
      toast.success('Household created — invite your family!');
      onReady();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create household');
    } finally { setBusy(false); }
  };

  const join = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/household/join`, { code: code.trim() });
      toast.success(`Welcome to the ${data.name} budget!`);
      onReady();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-lg mx-auto space-y-6" data-testid="budget-setup">
      <div className="text-center">
        <HeartHandshake className="w-12 h-12 text-[#B76E79] mx-auto mb-3" strokeWidth={1.5} />
        <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-[#36454F]">Family Budget</h1>
        <p className="mt-2 text-sm text-[#36454F]/60 font-['Manrope']">
          Budget together in real time — you and everyone you invite see the same
          envelopes and every expense as it happens.
        </p>
        <p className="mt-2 text-xs text-[#36454F]/50 font-['Manrope'] italic">
          Family Budget works on its own — you don't need to be running a credit dispute to use it.
        </p>
      </div>
      <Card className="border-[#36454F]/10 bg-white">
        <CardContent className="p-6 space-y-3">
          <p className="font-['Manrope'] font-semibold text-[#36454F]">Start a household budget</p>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Household name (e.g., The Browns)" data-testid="household-name-input" />
          <Button onClick={create} disabled={busy} className="w-full bg-[#B76E79] hover:bg-[#9e5c66] text-white rounded-sm" data-testid="create-household-btn">
            Create budget
          </Button>
        </CardContent>
      </Card>
      <Card className="border-[#36454F]/10 bg-white">
        <CardContent className="p-6 space-y-3">
          <p className="font-['Manrope'] font-semibold text-[#36454F]">Or join with an invite code</p>
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="6-character code" maxLength={6} data-testid="join-code-input" />
          <Button onClick={join} disabled={busy} variant="outline" className="w-full rounded-sm border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79]/10" data-testid="join-household-btn">
            Join household
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default function BudgetPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [hasHousehold, setHasHousehold] = useState(false);
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [income, setIncome] = useState('');
  const [expAmount, setExpAmount] = useState('');
  const [expNote, setExpNote] = useState('');
  const [expCat, setExpCat] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [newCat, setNewCat] = useState('');
  const [giving, setGiving] = useState(null);
  const [prayerOpen, setPrayerOpen] = useState(false);
  const editingRef = useRef(false);
  const verse = VERSES[new Date().getDate() % VERSES.length];

  const downloadCsv = async () => {
    try {
      const { data: token } = { data: localStorage.getItem('selah_access_token') };
      const res = await fetch(`${API_URL}/api/budget/${month}/export.csv`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) throw new Error('csv fetch failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `selah-slate-${month}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded.');
    } catch {
      toast.error('Could not download CSV');
    }
  };

  const load = useCallback(async (m, silent = false) => {
    try {
      const { data: d } = await axios.get(`${API_URL}/api/budget/${m}`);
      setData(d);
      setHasHousehold(true);
      if (!editingRef.current) setIncome(String(d.budget.income || ''));
    } catch (e) {
      if (e.response?.status === 404) setHasHousehold(false);
      else if (!silent) toast.error('Could not load budget');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(month); }, [month, load]);

  const loadGiving = useCallback(async () => {
    try {
      const { data: g } = await axios.get(`${API_URL}/api/budget/giving-summary/${month.slice(0, 4)}`);
      setGiving(g);
    } catch (e) { /* no household yet */ }
  }, [month]);

  useEffect(() => { if (hasHousehold) loadGiving(); }, [hasHousehold, month, loadGiving]);

  // near-real-time sync: refetch every 3s while the tab is visible
  useEffect(() => {
    if (!hasHousehold) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible' && !editingRef.current) load(month, true);
    }, 3000);
    return () => clearInterval(id);
  }, [hasHousehold, month, load]);

  const saveBudget = async (categories, incomeVal) => {
    try {
      await axios.put(`${API_URL}/api/budget/${month}`, {
        income: parseFloat(incomeVal || 0) || 0,
        categories: categories.map(({ id, name, allocated, is_giving }) => ({
          id, name, allocated: parseFloat(allocated || 0) || 0, is_giving: !!is_giving,
        })),
      });
      load(month, true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    }
  };

  const setAllocated = (catId, value) => {
    setData((d) => ({
      ...d,
      budget: { ...d.budget, categories: d.budget.categories.map((c) => (c.id === catId ? { ...c, allocated: value } : c)) },
    }));
  };

  const addExpense = async (e) => {
    e.preventDefault();
    if (!expCat || !expAmount) { toast.error('Pick a category and amount'); return; }
    const amount = parseFloat(expAmount);
    if (!Number.isFinite(amount) || amount <= 0) { toast.error('Enter a valid amount'); return; }
    const note = expNote;
    const category_id = expCat;
    try {
      const { data: created } = await axios.post(`${API_URL}/api/budget/${month}/expense`, {
        category_id, amount, note,
      });
      // Optimistic update: immediately reflect the new expense in the UI, so the user
      // sees "Spent" bump and the entry appear in the Activity feed even before the
      // background refetch (or the 3s polling) completes.
      setData((prev) => {
        if (!prev) return prev;
        const newExpense = {
          expense_id: created.expense_id,
          category_id: created.category_id,
          amount: created.amount,
          note: created.note,
          added_by_name: created.added_by_name,
          created_at: created.created_at,
          month: created.month,
        };
        const nextExpenses = [newExpense, ...(prev.expenses || [])].slice(0, 50);
        const nextCategories = (prev.budget?.categories || []).map((c) =>
          c.id === created.category_id
            ? { ...c, spent: round2((c.spent || 0) + created.amount) }
            : c
        );
        return {
          ...prev,
          expenses: nextExpenses,
          total_spent: round2((prev.total_spent || 0) + created.amount),
          budget: { ...prev.budget, categories: nextCategories },
        };
      });
      setExpAmount(''); setExpNote('');
      toast.success(`Expense added — $${amount.toFixed(2)}`);
      // Fire the authoritative refetch in the background; ignore failures because the
      // optimistic state is already correct and the 3s poller will re-sync anyway.
      load(month, true).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add expense');
    }
  };

  const deleteExpense = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/budget/expense/${id}`);
      load(month, true);
      loadGiving();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      const { data: d } = await axios.post(`${API_URL}/api/household/invite`, { email: inviteEmail.trim() });
      setInviteCode(d.code);
      setInviteEmail('');
      toast.success('Invite sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send invite');
    }
  };

  const addCategory = async () => {
    if (!newCat.trim() || !data) return;
    const categories = [...data.budget.categories, { id: '', name: newCat.trim(), allocated: 0, is_giving: false }];
    setNewCat('');
    await saveBudget(categories, income);
  };

  const removeCategory = async (catId) => {
    const cat = data.budget.categories.find((c) => c.id === catId);
    if (cat?.spent > 0 && !window.confirm(`"${cat.name}" has ${fmt(cat.spent)} in logged expenses. Remove it anyway?`)) return;
    const categories = data.budget.categories.filter((c) => c.id !== catId);
    await saveBudget(categories, income);
  };

  const b = data?.budget;
  const totalAllocated = b ? b.categories.reduce((s, c) => s + (parseFloat(c.allocated) || 0), 0) : 0;
  const totalSpent = data?.total_spent || 0;
  const catName = (id) => b?.categories.find((c) => c.id === id)?.name || '—';

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="budget-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">Selah Slate&trade;</span>
            </Link>
            <Link to="/dashboard" className="text-sm text-[#36454F]/70 hover:text-[#36454F] font-['Manrope'] flex items-center" data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1.5} />Dashboard
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <p className="text-center text-[#36454F]/50 font-['Manrope'] py-20">Loading…</p>
        ) : !hasHousehold ? (
          <SetupView onReady={() => { setLoading(true); load(month); }} prefillCode={searchParams.get('code') || ''} />
        ) : (
          <div className="space-y-6">
            <div className="text-center">
              <p className="font-['Playfair_Display'] italic text-[#36454F]/70" data-testid="budget-verse">{verse.t}</p>
              <p className="text-xs text-[#36454F]/45 font-['Manrope'] mt-1">— {verse.r}</p>
            </div>

            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-[#36454F] truncate" data-testid="household-name">
                  {data.household.name}
                </h1>
                <p className="text-xs text-[#36454F]/50 font-['Manrope'] flex items-center mt-1 min-w-0">
                  <Users className="w-3.5 h-3.5 mr-1 flex-shrink-0" strokeWidth={1.5} />
                  <span className="truncate">{data.members.map((m) => m.name.split(' ')[0]).join(', ')} · syncing live</span>
                </p>
              </div>
              <div className="flex items-center gap-2 font-['Manrope'] flex-shrink-0" data-testid="month-nav">
                <button onClick={() => setMonth(shiftMonth(month, -1))} className="p-2 hover:bg-[#36454F]/5 rounded-sm" data-testid="prev-month-btn" aria-label="Previous month">
                  <ChevronLeft className="w-4 h-4 text-[#36454F]" />
                </button>
                <span className="text-sm font-semibold text-[#36454F] w-32 text-center">{monthLabel(month)}</span>
                <button onClick={() => setMonth(shiftMonth(month, 1))} className="p-2 hover:bg-[#36454F]/5 rounded-sm" data-testid="next-month-btn" aria-label="Next month">
                  <ChevronRight className="w-4 h-4 text-[#36454F]" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3" data-testid="budget-summary">
              {[
                { label: 'Income', value: b.income },
                { label: 'Budgeted', value: totalAllocated },
                { label: 'Spent', value: totalSpent },
              ].map((s) => (
                <Card key={s.label} className="border-[#36454F]/10 bg-white">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs uppercase tracking-wider text-[#36454F]/45 font-['Manrope']">{s.label}</p>
                    <p className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mt-1">{fmt(s.value)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Faith + Export toolbar — the "no one else has this" row */}
            <div className="flex flex-wrap gap-2 justify-center" data-testid="budget-toolbar">
              <Button
                onClick={() => setPrayerOpen(true)}
                className="h-9 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm text-sm"
                data-testid="pray-btn"
              >
                <Sparkles className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Pray over this month
              </Button>
              <Button
                onClick={() => window.open(`/budget/print/${month}`, '_blank')}
                variant="outline"
                className="h-9 border-[#36454F]/20 text-[#36454F] hover:bg-[#36454F]/5 font-['Manrope'] rounded-sm text-sm"
                data-testid="print-budget-btn"
              >
                <Printer className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Print
              </Button>
              <Button
                onClick={downloadCsv}
                variant="outline"
                className="h-9 border-[#36454F]/20 text-[#36454F] hover:bg-[#36454F]/5 font-['Manrope'] rounded-sm text-sm"
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> CSV
              </Button>
              <Link to={`/giving/statement/${month.slice(0, 4)}`}>
                <Button
                  variant="outline"
                  className="h-9 border-[#B76E79]/40 text-[#B76E79] hover:bg-[#B76E79]/8 font-['Manrope'] rounded-sm text-sm"
                  data-testid="statement-btn"
                >
                  <ScrollText className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Statement of Generosity
                </Button>
              </Link>
            </div>

            <HouseholdCouncilCard month={month} membersCount={data.members?.length || 1} />

            {giving && giving.total > 0 && (
              <Card className="border-[#B76E79]/30 bg-gradient-to-r from-[#B76E79]/10 to-transparent" data-testid="giving-impact-card">
                <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#B76E79] font-['Manrope'] font-semibold">
                      ✝️ Giving Impact · {giving.year}
                    </p>
                    <p className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mt-1" data-testid="giving-total">
                      {fmt(giving.total)} given this year
                    </p>
                  </div>
                  <p className="text-xs italic text-[#36454F]/55 font-['Manrope'] max-w-xs sm:text-right">
                    "A generous person will prosper; whoever refreshes others will be refreshed." — Proverbs 11:25
                  </p>
                </CardContent>
              </Card>
            )}

            <Card className="border-[#36454F]/10 bg-white">
              <CardContent className="p-5 flex items-center gap-3">
                <label className="text-sm font-['Manrope'] font-semibold text-[#36454F] whitespace-nowrap" htmlFor="income-input">
                  Monthly income
                </label>
                <Input
                  id="income-input"
                  type="number"
                  min="0"
                  value={income}
                  onFocus={() => { editingRef.current = true; }}
                  onChange={(e) => setIncome(e.target.value)}
                  onBlur={() => { editingRef.current = false; saveBudget(b.categories, income); }}
                  placeholder="0.00"
                  className="max-w-[180px]"
                  data-testid="income-input"
                />
              </CardContent>
            </Card>

            <Card className="border-[#36454F]/10 bg-white" data-testid="envelopes-card">
              <CardContent className="p-5 space-y-4">
                <p className="font-['Manrope'] font-semibold text-[#36454F]">Envelopes</p>
                {b.categories.map((c) => {
                  const remaining = (parseFloat(c.allocated) || 0) - (c.spent || 0);
                  const pct = c.allocated > 0 ? Math.min(100, ((c.spent || 0) / c.allocated) * 100) : 0;
                  return (
                    <div key={c.id} data-testid={`envelope-${c.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <span className="text-sm font-['Manrope'] text-[#36454F] flex items-center min-w-0 flex-1">
                          {c.is_giving && <span className="mr-1.5 flex-shrink-0" title="Giving">✝️</span>}
                          <span className="truncate">{c.name}</span>
                        </span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-xs font-['Manrope'] whitespace-nowrap ${remaining < 0 ? 'text-red-600 font-semibold' : 'text-[#36454F]/50'}`}>
                            {fmt(c.spent || 0)} of
                          </span>
                          <Input
                            type="number"
                            min="0"
                            value={c.allocated}
                            onFocus={() => { editingRef.current = true; }}
                            onChange={(e) => setAllocated(c.id, e.target.value)}
                            onBlur={() => { editingRef.current = false; saveBudget(b.categories, income); }}
                            className="w-24 h-8 text-sm"
                            data-testid={`allocated-input-${c.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                          />
                          <button onClick={() => removeCategory(c.id)} className="p-1 text-[#36454F]/25 hover:text-red-500" aria-label={`Remove ${c.name}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="h-2 bg-[#36454F]/8 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${remaining < 0 ? 'bg-red-400' : 'bg-[#B76E79]'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
                <div className="flex gap-2 pt-1">
                  <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New envelope name" className="h-9" data-testid="new-category-input" />
                  <Button onClick={addCategory} variant="outline" className="h-9 rounded-sm border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79]/10" data-testid="add-category-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#B76E79]/30 bg-white" data-testid="add-expense-card">
              <CardContent className="p-5">
                <p className="font-['Manrope'] font-semibold text-[#36454F] mb-3">Log an expense</p>
                <form onSubmit={addExpense} className="flex flex-wrap gap-2">
                  <select
                    value={expCat}
                    onChange={(e) => setExpCat(e.target.value)}
                    className="h-9 px-2 text-sm border border-input rounded-md bg-white font-['Manrope'] text-[#36454F] flex-1 min-w-[140px]"
                    data-testid="expense-category-select"
                  >
                    <option value="">Category…</option>
                    {b.categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <Input type="number" min="0.01" step="0.01" value={expAmount} onChange={(e) => setExpAmount(e.target.value)} placeholder="Amount" className="h-9 w-28" data-testid="expense-amount-input" />
                  <Input value={expNote} onChange={(e) => setExpNote(e.target.value)} placeholder="Note (optional)" className="h-9 flex-1 min-w-[120px]" data-testid="expense-note-input" />
                  <Button type="submit" className="h-9 bg-[#B76E79] hover:bg-[#9e5c66] text-white rounded-sm" data-testid="add-expense-btn">Add</Button>
                </form>
              </CardContent>
            </Card>

            <Card className="border-[#36454F]/10 bg-white" data-testid="expense-feed">
              <CardContent className="p-5">
                <p className="font-['Manrope'] font-semibold text-[#36454F] mb-3">Activity feed</p>
                {data.expenses.length === 0 ? (
                  <p className="text-sm text-[#36454F]/45 font-['Manrope']">No expenses yet this month.</p>
                ) : (
                  <div className="space-y-2">
                    {data.expenses.map((e) => (
                      <div key={e.expense_id} className="text-sm font-['Manrope'] border-b border-[#36454F]/5 pb-2" data-testid={`expense-row-${e.expense_id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <span className="font-semibold text-[#36454F]">{e.added_by_name?.split(' ')[0] || 'Someone'}</span>
                            <span className="text-[#36454F]/60 break-words"> · {catName(e.category_id)}{e.note ? ` — ${e.note}` : ''}</span>
                            <span className="text-[#36454F]/35 text-xs ml-2 whitespace-nowrap">
                              {new Date(e.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="font-semibold text-[#36454F] whitespace-nowrap">{fmt(e.amount)}</span>
                            <button onClick={() => deleteExpense(e.expense_id)} className="p-1 text-[#36454F]/25 hover:text-red-500" aria-label="Delete expense" data-testid={`delete-expense-${e.expense_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        <ContentmentTags expenseId={e.expense_id} current={e.contentment} />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-[#36454F]/10 bg-[#36454F]" data-testid="invite-card">
              <CardContent className="p-5">
                <p className="font-['Manrope'] font-semibold text-[#E0BFB8] mb-1 flex items-center">
                  <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />Invite family to this budget
                </p>
                <p className="text-xs text-white/60 font-['Manrope'] mb-3">They'll see every envelope and expense, live.</p>
                <div className="flex gap-2">
                  <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} type="email" placeholder="their@email.com" className="h-9 bg-white" data-testid="invite-email-input" />
                  <Button onClick={sendInvite} className="h-9 bg-[#B76E79] hover:bg-[#9e5c66] text-white rounded-sm" data-testid="send-invite-btn">Invite</Button>
                </div>
                {inviteCode && (
                  <p className="text-sm text-white/85 font-['Manrope'] mt-3" data-testid="invite-code-display">
                    Invite sent! Their code: <span className="font-bold tracking-widest text-[#E0BFB8]">{inviteCode}</span>
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
      <BudgetPrayerModal month={month} open={prayerOpen} onClose={() => setPrayerOpen(false)} />
      <AppFooter />
    </div>
  );
}
